import { authService } from './authService';
import { withOrgHeader } from './apiContext';
import { mockSellerFlowApiClient } from './mockSellerFlowApi';

const API_BASE_URL = import.meta.env.VITE_VIDEO_API_BASE_URL || 'http://localhost:5001';

export class SellerFlowApiError extends Error {
  constructor(message, statusCode = 0, details = null) {
    super(message);
    this.name = 'SellerFlowApiError';
    this.statusCode = statusCode;
    this.details = details;
  }

  get isNotFound() {
    return this.statusCode === 404;
  }

  get isPermissionDenied() {
    return this.statusCode === 403;
  }

  get detailCode() {
    if (typeof this.details?.detail === 'object' && this.details?.detail?.code) {
      return this.details.detail.code;
    }
    return this.details?.code || null;
  }

  get detailMessage() {
    if (typeof this.details?.detail === 'object' && this.details?.detail?.message) {
      return this.details.detail.message;
    }
    if (typeof this.details?.detail === 'string') {
      return this.details.detail;
    }
    return this.details?.message || this.message;
  }

  get isBusinessProfileEnrichmentIncomplete() {
    return this.statusCode === 409 && this.detailCode === 'business_profile_enrichment_incomplete';
  }

  get currentBusinessProfileStatus() {
    if (typeof this.details?.detail === 'object' && this.details?.detail?.current_status) {
      return this.details.detail.current_status;
    }
    return this.details?.current_status || null;
  }

  get isCreativeDeleteConflict() {
    return this.statusCode === 409 && [
      'creative_not_draft_deletable',
      'selected_on_workspace',
      'referenced_by_proposal',
      'referenced_by_agreement',
      'referenced_by_campaign',
    ].includes(this.detailCode);
  }

  get creativeDeleteConflictMessage() {
    switch (this.detailCode) {
      case 'creative_not_draft_deletable':
        return 'Only draft creatives can be deleted.';
      case 'selected_on_workspace':
        return 'This creative is currently selected on the workspace and cannot be deleted.';
      case 'referenced_by_proposal':
        return 'This creative is already referenced by a proposal and cannot be deleted.';
      case 'referenced_by_agreement':
        return 'This creative is already referenced by an agreement and cannot be deleted.';
      case 'referenced_by_campaign':
        return 'This creative is already referenced by a campaign and cannot be deleted.';
      default:
        return this.detailMessage || 'This creative could not be deleted.';
    }
  }

  get isPublicProposalInvalid() {
    return this.statusCode === 404 && this.detailCode === 'invalid_share_link';
  }

  get isPublicProposalExpired() {
    return this.statusCode === 410 && this.detailCode === 'expired_share_link';
  }

  get isPublicProposalRevoked() {
    return this.statusCode === 410 && this.detailCode === 'revoked_share_link';
  }

  get publicProposalState() {
    if (this.isPublicProposalInvalid) return 'invalid';
    if (this.isPublicProposalExpired) return 'expired';
    if (this.isPublicProposalRevoked) return 'revoked';
    return null;
  }
}

class SellerFlowApiClient {
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || API_BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  async _makeRequest(path, options = {}, requestOptions = {}) {
    const url = `${this.baseUrl}${path}`;
    const {
      authRequired = true,
      credentials = authRequired ? 'include' : 'omit',
      // Public endpoints (share links, etc.) must not leak the active org id;
      // default includeOrg to false whenever auth is also disabled.
      includeOrg = authRequired,
    } = requestOptions;

    const headers = withOrgHeader(
      { ...this.headers, ...(options.headers || {}) },
      { includeOrg },
    );

    try {
      const response = await fetch(url, {
        credentials,
        ...options,
        headers,
      });

      if (response.status === 401 && authRequired) {
        authService.handleUnauthorized();
        throw new SellerFlowApiError('Unauthorized', 401, { redirect: true });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = typeof errorData.detail === 'object'
          ? errorData.detail?.message || errorData.message || `HTTP ${response.status}`
          : errorData.detail || errorData.message || `HTTP ${response.status}`;
        throw new SellerFlowApiError(
          message,
          response.status,
          errorData,
        );
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      if (error instanceof SellerFlowApiError) throw error;
      throw new SellerFlowApiError(`Network error: ${error.message}`, 0, error);
    }
  }

  getSellerHomeSummary() {
    return this._makeRequest('/api/v1/seller-home/summary');
  }

  getSellerHomeQueue(filters = {}) {
    const params = new URLSearchParams();
    if (filters.stage) params.append('stage', filters.stage);
    if (filters.needsAttention) params.append('needs_attention', 'true');
    if (filters.stale) params.append('stale', 'true');
    if (filters.hasLiveCampaign) params.append('has_live_campaign', 'true');
    const query = params.toString();
    return this._makeRequest(`/api/v1/seller-home/queue${query ? `?${query}` : ''}`);
  }

  ensureWorkspace(advertiserId, originJson = null) {
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}/workspace`, {
      method: 'PUT',
      body: JSON.stringify(originJson ? { origin_json: originJson } : {}),
    });
  }

  getActiveWorkspace(advertiserId) {
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}/workspace`);
  }

  getWorkspace(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}`);
  }

  bootstrapPitchKit(workspaceId, options = {}) {
    const payload = {};
    if (options.force_new === true || options.forceNew === true) {
      payload.force_new = true;
    }

    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/pitch-bootstrap`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  getAdvertiserBusinessProfile(advertiserId) {
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}/business-profile`);
  }

  getWorkspaceBusinessProfile(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/business-profile`);
  }

  enrichAdvertiserBusinessProfile(advertiserId, payload = null) {
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}/business-profile/enrich`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  enrichWorkspaceBusinessProfile(workspaceId, payload = null) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/business-profile/enrich`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  getWorkspaceQueueSummary(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/queue-summary`);
  }

  getWorkspaceActivity(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/activity`);
  }

  listAdvertiserCreatives(advertiserId, workspaceId = null) {
    const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}/creatives${query}`);
  }

  getWorkspaceCreatives(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives`);
  }

  getWorkspaceCreative(workspaceId, bindingId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives/${bindingId}`);
  }

  updateWorkspaceCreative(workspaceId, bindingId, payload) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives/${bindingId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  deleteWorkspaceCreative(workspaceId, bindingId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives/${bindingId}`, {
      method: 'DELETE',
    });
  }

  retryWorkspaceCreative(workspaceId, bindingId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives/${bindingId}/retry`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  generateWorkspaceCreative(workspaceId, payload) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creatives/generate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  selectWorkspaceCreative(workspaceId, bindingId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/creative-selection`, {
      method: 'PUT',
      body: JSON.stringify({ binding_id: bindingId }),
    });
  }

  getOfferConfig(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/offer-config`);
  }

  upsertOfferConfig(workspaceId, config) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/offer-config`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  listProposals(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/proposals`);
  }

  createProposal(workspaceId, payload) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/proposals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  getProposal(proposalId) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}`);
  }

  patchProposal(proposalId, payload) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  shareProposal(proposalId, payload = {}) {
    const requestPayload = {};

    if (payload.force_new === true || payload.forceNew === true) {
      requestPayload.force_new = true;
    }
    if (payload.recipient_email || payload.recipientEmail) {
      requestPayload.recipient_email = payload.recipient_email || payload.recipientEmail;
    }
    if (payload.delivery_channel || payload.deliveryChannel) {
      requestPayload.delivery_channel = payload.delivery_channel || payload.deliveryChannel;
    }

    return this._makeRequest(`/api/v1/proposals/${proposalId}/share`, {
      method: 'POST',
      body: JSON.stringify(requestPayload),
    });
  }

  sendProposalWithPayload(proposalId, payload = {}) {
    const requestPayload = {};

    if (payload.recipient_email || payload.recipientEmail) {
      requestPayload.recipient_email = payload.recipient_email || payload.recipientEmail;
    }
    if (payload.force_new_share === true || payload.forceNewShare === true) {
      requestPayload.force_new_share = true;
    }

    return this._makeRequest(`/api/v1/proposals/${proposalId}/send`, {
      method: 'POST',
      body: JSON.stringify(requestPayload),
    });
  }

  sendProposal(proposalId, payload = {}) {
    return this.sendProposalWithPayload(proposalId, payload);
  }

  getPublicProposal(token) {
    return this._makeRequest(`/api/v1/public/proposals/${token}`, {}, {
      authRequired: false,
      credentials: 'omit',
    });
  }

  recordProposalResponse(proposalId, payload) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}/response`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  createProposalRevision(proposalId, detail = null) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}/revisions`, {
      method: 'POST',
      body: JSON.stringify({ detail }),
    });
  }

  getAgreementForProposal(proposalId) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}/agreement`);
  }

  createAgreementForProposal(proposalId) {
    return this._makeRequest(`/api/v1/proposals/${proposalId}/agreement`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  getWorkspaceAgreement(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/agreement`);
  }

  getAgreement(agreementId) {
    return this._makeRequest(`/api/v1/agreements/${agreementId}`);
  }

  startBilling(agreementId, payload = {}) {
    return this._makeRequest(`/api/v1/agreements/${agreementId}/billing/start`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  confirmBilling(agreementId, payload = {}) {
    return this._makeRequest(`/api/v1/agreements/${agreementId}/billing/confirm`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  failBilling(agreementId, payload = {}) {
    return this._makeRequest(`/api/v1/agreements/${agreementId}/billing/fail`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  cancelBilling(agreementId, payload = {}) {
    return this._makeRequest(`/api/v1/agreements/${agreementId}/billing/cancel`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  getLaunchReadiness(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/launch-readiness`);
  }

  launchCampaign(workspaceId, payload = {}) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/campaigns/launch`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  getWorkspaceCampaign(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/campaign`);
  }

  getCampaign(campaignId) {
    return this._makeRequest(`/api/v1/campaigns/${campaignId}`);
  }

  getWorkspaceCampaignReportSummary(workspaceId) {
    return this._makeRequest(`/api/v1/workspaces/${workspaceId}/campaign/report-summary`);
  }

  getCampaignReportSummary(campaignId) {
    return this._makeRequest(`/api/v1/campaigns/${campaignId}/report-summary`);
  }

  getLatestReportSnapshot(campaignId) {
    return this._makeRequest(`/api/v1/campaigns/${campaignId}/report-snapshots/latest`);
  }

  listReportSnapshots(campaignId) {
    return this._makeRequest(`/api/v1/campaigns/${campaignId}/report-snapshots`);
  }
}

// When VITE_ENABLE_MOCK_GENERATION=true, swap in the mock client so the
// portal works end-to-end with no backend (used for screencasts and demo).
const __useMockSellerFlow = (
  typeof import.meta !== 'undefined'
  && import.meta.env
  && import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true'
);

export const sellerFlowApiClient = __useMockSellerFlow
  ? mockSellerFlowApiClient
  : new SellerFlowApiClient();
export default sellerFlowApiClient;
